import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { useCheckEmployeeAdditionalPasswordMutation } from '@/services';
import { catchError, messages } from '@/utils/helpers';
import { useAuth } from '@/hooks';

export interface CheckEmployeeAdditionalPasswordProps extends DialogProps {
  onCheck(): void;
  onClose(): void;
}

const CheckEmployeeAdditionalPassword: React.FC<CheckEmployeeAdditionalPasswordProps> = ({
  onCheck,
  onClose,
  ...props
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { user } = useAuth();

  const [checkEmployeeAdditionalPassword, checkEmployeeAdditionalPasswordResponse] =
    useCheckEmployeeAdditionalPasswordMutation();

  const { values, errors, touched, handleSubmit, handleChange } = useFormik({
    initialValues: { additional_password: '' },
    validationSchema: yup.object().shape({
      additional_password: yup.string().required(messages.form.REQUIRED).min(8, messages.form.MIN_LENGTH()),
    }),
    async onSubmit({ additional_password }, { resetForm }) {
      try {
        await checkEmployeeAdditionalPassword({ id: user?.employee?.id, additional_password }).unwrap();

        onCheck();
        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(catchError(error).error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Пароль для проведения операций</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid item xs={12}>
            <TextField
              type="password"
              name="additional_password"
              autoComplete="off"
              label="Пароль"
              placeholder="Введите значение"
              value={values.additional_password}
              onChange={handleChange}
              disabled={checkEmployeeAdditionalPasswordResponse.isLoading}
              error={touched.additional_password && Boolean(errors.additional_password)}
              helperText={touched.additional_password ? errors.additional_password : ''}
              fullWidth
            />
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={checkEmployeeAdditionalPasswordResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={checkEmployeeAdditionalPasswordResponse.isLoading}>
            Проверить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CheckEmployeeAdditionalPassword;
